import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { X, GripHorizontal, GripVertical, ChevronLeft, ChevronRight } from 'lucide-react';
import { UnsavedSaveSection } from '../shared/UnsavedSaveSection';
import { ElevationProfile } from '../MeasureTool/ElevationProfile';
import { colorByOptionViews } from '../../utils/colorByOptions';
import { useLayerReadiness } from '../../hooks/useLayerReadiness';
import { interpolateAlongCartesianPolyline } from '../../utils/polylineInterpolation';
import { formatDistance } from '../../utils/units';
import { useDialogResize } from '../../hooks/useDialogResize';
export const PathElevationPopout = ({ path, segmentIndex: initialSegmentIndex, onClose, onUnitChange, onHighlightSegment, initialPosition, initialWidth, onLayoutChange, onHoverPosition, externalCrosshairFraction, onChangeColorBy, onSave, saveLabel = 'Save', saveTitle, inlineSave, }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const [showSaveSection, setShowSaveSection] = useState(false);
    // ESC key to close
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);
    const [position, setPosition] = useState(() => {
        if (initialPosition) {
            return initialPosition;
        }
        return {
            x: Math.max(50, window.innerWidth / 2 - 175),
            y: Math.max(50, window.innerHeight / 2 - 200)
        };
    });
    const { width, startLeftResize, startRightResize } = useDialogResize({
        initialWidth: initialWidth !== null && initialWidth !== void 0 ? initialWidth : 380,
        minWidth: 320,
        position,
        setPosition,
    });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [currentView, setCurrentView] = useState(initialSegmentIndex === null ? 'total' : initialSegmentIndex);
    // Mirror PathOptionsDialog: source of truth is path.colorBy, falling back
    // to local state when no parent handler is wired.
    const [localColorBy, setLocalColorBy] = useState('elevation');
    const colorBy = (_a = path.colorBy) !== null && _a !== void 0 ? _a : (onChangeColorBy ? 'elevation' : localColorBy);
    const setColorBy = (mode) => {
        if (onChangeColorBy)
            onChangeColorBy(path.id, mode);
        else
            setLocalColorBy(mode);
    };
    const layerState = useLayerReadiness();
    const windowRef = useRef(null);
    useEffect(() => {
        onLayoutChange === null || onLayoutChange === void 0 ? void 0 : onLayoutChange({ position, width, view: currentView });
    }, [position, width, currentView, onLayoutChange]);
    const handleMouseDown = useCallback((e) => {
        if (windowRef.current) {
            const rect = windowRef.current.getBoundingClientRect();
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
            setIsDragging(true);
        }
    }, []);
    const handleMouseMove = useCallback((e) => {
        if (isDragging) {
            const newX = Math.max(0, Math.min(window.innerWidth - 380, e.clientX - dragOffset.x));
            const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y));
            setPosition({ x: newX, y: newY });
        }
    }, [isDragging, dragOffset]);
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);
    // Highlight segment when view changes
    useEffect(() => {
        if (onHighlightSegment) {
            const segmentIndex = currentView === 'total' ? null : currentView;
            onHighlightSegment(path.id, segmentIndex);
            // Clear highlight when popout closes
            return () => {
                onHighlightSegment(path.id, null);
            };
        }
    }, [currentView, path.id, onHighlightSegment]);
    const currentLinePositions = useMemo(() => {
        if (currentView === 'total') {
            return path.points.map(p => p.position);
        }
        const seg = path.segments[currentView];
        if (!seg)
            return [];
        const startIdx = path.points.findIndex(p => p.id === seg.startPointId);
        const endIdx = path.points.findIndex(p => p.id === seg.endPointId);
        if (startIdx === -1 || endIdx === -1)
            return [];
        return [path.points[startIdx].position, path.points[endIdx].position];
    }, [currentView, path.points, path.segments]);
    const handleHoverFraction = useCallback((fraction) => {
        if (!onHoverPosition)
            return;
        if (fraction === null) {
            onHoverPosition(null, path.color);
            return;
        }
        const pos = interpolateAlongCartesianPolyline(currentLinePositions, fraction);
        onHoverPosition(pos, path.color);
    }, [onHoverPosition, currentLinePositions, path.color]);
    const currentProfile = currentView === 'total'
        ? path.totalElevationProfile
        : (_b = path.segments[currentView]) === null || _b === void 0 ? void 0 : _b.elevationProfile;
    const currentDistance = currentView === 'total'
        ? path.totalDistanceMeters
        : ((_c = path.segments[currentView]) === null || _c === void 0 ? void 0 : _c.distanceMeters) || 0;
    const currentDistance3d = currentView === 'total'
        ? ((_d = path.totalElevationProfile) === null || _d === void 0 ? void 0 : _d.totalDistance3dMeters) || path.totalDistanceMeters
        : ((_f = (_e = path.segments[currentView]) === null || _e === void 0 ? void 0 : _e.elevationProfile) === null || _f === void 0 ? void 0 : _f.totalDistance3dMeters) || ((_g = path.segments[currentView]) === null || _g === void 0 ? void 0 : _g.distanceMeters) || 0;
    const viewLabel = currentView === 'total'
        ? 'Total Path'
        : `Segment ${currentView + 1}`;
    const handlePrevSegment = () => {
        if (currentView === 'total') {
            setCurrentView(path.segments.length - 1);
        }
        else if (currentView === 0) {
            setCurrentView('total');
        }
        else {
            setCurrentView(currentView - 1);
        }
    };
    const handleNextSegment = () => {
        if (currentView === 'total') {
            setCurrentView(0);
        }
        else if (currentView === path.segments.length - 1) {
            setCurrentView('total');
        }
        else {
            setCurrentView(currentView + 1);
        }
    };
    return (_jsxs("div", { ref: windowRef, className: "fixed z-[65] rounded-lg shadow-2xl", style: {
            left: position.x,
            top: position.y,
            width,
            backgroundColor: 'var(--charcoal)',
            border: `2px solid ${path.color}`,
            position: 'fixed',
            userSelect: isDragging ? 'none' : 'auto'
        }, children: [_jsxs("div", { className: "flex items-center justify-between px-3 py-2 cursor-move rounded-t-lg", style: { backgroundColor: 'var(--pewter)' }, onMouseDown: handleMouseDown, children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(GripHorizontal, { size: 14, style: { color: 'var(--dew)', opacity: 0.5 } }), _jsx("span", { className: "text-sm font-semibold", style: { color: 'var(--sand)' }, children: path.name })] }), _jsxs("div", { className: "flex items-center gap-1", children: [(onSave || inlineSave) && (_jsx("button", { onClick: inlineSave ? () => setShowSaveSection(s => !s) : onSave, onMouseDown: (e) => e.stopPropagation(), className: "px-2 py-0.5 rounded text-xs font-medium", style: { backgroundColor: 'var(--sand)', color: 'var(--forest)' }, title: saveTitle, children: saveLabel })), _jsx("button", { onClick: onClose, className: "p-1 rounded hover:bg-opacity-20 hover:bg-gray-500 transition-colors", style: { color: 'var(--dew)' }, onMouseDown: (e) => e.stopPropagation(), children: _jsx(X, { size: 16 }) })] })] }), inlineSave && showSaveSection && (_jsx("div", { className: "px-3 pb-3 border-b", style: { borderColor: 'var(--pewter)' }, onMouseDown: (e) => e.stopPropagation(), children: _jsx(UnsavedSaveSection, { itemId: path.id, itemType: "path", currentName: path.name, onChangeName: inlineSave.onChangeName, onRemove: inlineSave.onRemove, onClose: () => setShowSaveSection(false), userFolders: inlineSave.userFolders, onCreateFolder: inlineSave.onCreateFolder, onSaveItem: inlineSave.onSaveItem, defaultFolderColor: path.color }) })), path.segments.length > 1 && (_jsxs("div", { className: "flex items-center justify-between px-3 py-2 border-b", style: { borderColor: 'var(--pewter)' }, onMouseDown: (e) => e.stopPropagation(), children: [_jsx("button", { onClick: handlePrevSegment, className: "p-1 rounded hover:bg-opacity-20", style: { color: 'var(--dew)' }, children: _jsx(ChevronLeft, { size: 16 }) }), _jsxs("div", { className: "flex flex-col items-center", children: [_jsx("span", { className: "text-xs font-medium", style: { color: 'var(--sand)' }, children: viewLabel }), _jsxs("div", { className: "grid grid-cols-2 gap-x-4 text-xs", style: { color: 'var(--dew)' }, children: [_jsxs("span", { children: [_jsx("span", { style: { color: 'var(--sand)' }, children: formatDistance(currentDistance3d, path.displayUnit) }), " terrain"] }), _jsxs("span", { children: [_jsx("span", { style: { color: 'var(--sand)' }, children: formatDistance(currentDistance, path.displayUnit) }), " linear"] })] })] }), _jsx("button", { onClick: handleNextSegment, className: "p-1 rounded hover:bg-opacity-20", style: { color: 'var(--dew)' }, children: _jsx(ChevronRight, { size: 16 }) })] })), _jsxs("div", { className: "flex items-center justify-end gap-2 px-3 pt-2", children: [_jsx("select", { value: colorBy, onChange: (e) => setColorBy(e.target.value), className: "px-2 py-1 rounded text-xs cursor-pointer", style: {
                            backgroundColor: 'var(--pewter)',
                            color: 'var(--sand)',
                            border: '1px solid var(--charcoal)',
                        }, title: "Colour the elevation curve by", children: colorByOptionViews(layerState).map(opt => (_jsx("option", { value: opt.value, disabled: opt.disabled, children: opt.label }, opt.value))) }), onUnitChange && (_jsxs("select", { value: path.displayUnit, onChange: (e) => onUnitChange(e.target.value), className: "px-2 py-1 rounded text-xs cursor-pointer", style: {
                            backgroundColor: 'var(--pewter)',
                            color: 'var(--sand)',
                            border: '1px solid var(--charcoal)',
                            minWidth: 86,
                        }, title: "Display unit", children: [_jsx("option", { value: "m", children: "Meters" }), _jsx("option", { value: "km", children: "Kilometers" }), _jsx("option", { value: "ft", children: "Feet" }), _jsx("option", { value: "yd", children: "Yards" }), _jsx("option", { value: "mi", children: "Miles" })] }))] }), _jsx("div", { className: "p-4", children: _jsx(ElevationProfile, { elevationProfile: currentProfile, unit: path.displayUnit, color: path.color, hideMeasurementSection: true, colorBy: colorBy, onHoverFraction: onHoverPosition ? handleHoverFraction : undefined, externalCrosshairFraction: externalCrosshairFraction }) }), _jsx("div", { onMouseDown: startLeftResize, title: "Drag to resize width", style: {
                    position: 'absolute', left: 0, bottom: 0,
                    width: 16, height: 16,
                    cursor: 'ew-resize', zIndex: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }, children: _jsx(GripVertical, { size: 12, style: { color: 'var(--dew)', opacity: 0.6 } }) }), _jsx("div", { onMouseDown: startRightResize, title: "Drag to resize width", style: {
                    position: 'absolute', right: 0, bottom: 0,
                    width: 16, height: 16,
                    cursor: 'ew-resize', zIndex: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }, children: _jsx(GripVertical, { size: 12, style: { color: 'var(--dew)', opacity: 0.6 } }) })] }));
};
